import * as React from "react";
import {
  Await,
  defer,
  LoaderFunctionArgs,
  useLoaderData,
  useSearchParams,
} from "react-router-dom";
import { useLanguage } from "../../Language";
import {
  isLoanHistorySortType,
  LoanHistorySortControl,
  LoanHistorySortType,
} from "./HostSystemItemSortControl";
import { getValidatedPage } from "./getValidatedPage";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import {
  Alert,
  Box,
  Divider,
  Pagination,
  SelectChangeEvent,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import axios from "axios";
import { LSResultItemSkeleton } from "../search/ls/LSResultItem/LSResultItemSkeleton";
import { LoanHistoryItem } from "./LoanHistoryItem";
import { GetLoanHistoryReturn } from "@tlc/ls2pac/dto/GetLoanHistoryReturn";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";

export interface ChildHistoryLoaderData {
  id: string;
  page: number;
  sort: string;
  pageSize: number;
  totalLoans: number;
  loanHistory: GetLoanHistoryReturn["loanHistory"];
}

export async function childHistoryLoader({
  params,
  request,
}: LoaderFunctionArgs) {
  const pageSize: number = 10;
  const id = params.id!;
  const searchParams = new URL(request.url).searchParams;
  const page = getValidatedPage(searchParams.get("page"));
  const sortParam = searchParams.get("sort");
  const sort = isLoanHistorySortType(sortParam)
    ? sortParam
    : LoanHistorySortType[0];

  const dataPromise = axios
    .get<GetLoanHistoryReturn>(
      `/loans/child/${id}/history/${
        page * pageSize - pageSize
      }/${pageSize}/${sort}`
    )
    .then(({ data }) => ({
      id,
      page,
      sort,
      pageSize,
      totalLoans: data.totalLoans,
      loanHistory: data.loanHistory,
    }))
    .catch((error) => {
      console.error("Error fetching loan history", error);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Response("Forbidden", { status: 403 });
      }
      throw error;
    });

  return defer({ data: dataPromise });
}

export const ChildHistoryView: React.FC = () => {
  const { data } = useLoaderData() as { data: Promise<ChildHistoryLoaderData> };
  const { translate } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <React.Suspense
      fallback={
        <>
          {Array.from({ length: 10 }).map((_, index) => (
            <LSResultItemSkeleton key={index} hideActions hideCheckbox />
          ))}
        </>
      }
    >
      <Await resolve={data}>
        {(resolved) => {
          const { page, pageSize, sort, totalLoans, loanHistory } = resolved;
          const pages = totalLoans ? Math.ceil(totalLoans / pageSize) : 0;

          const handlePageChange = (
            _event: React.ChangeEvent<unknown>,
            newPage: number
          ) => {
            // TODO is directly  mutating searchParams problematic?
            searchParams.set("page", newPage.toString());
            setSearchParams(searchParams);
          };

          const handleSortChange = (event: SelectChangeEvent) => {
            const newSort = event.target.value as HostSystemItemSortType;
            // TODO is directly  mutating searchParams problematic?
            searchParams.set("page", "1");
            searchParams.set("sort", newSort);
            setSearchParams(searchParams);
          };

          return (
            <>
              {totalLoans === 0 ? (
                <Alert sx={{ mt: 2 }} severity="info">
                  {translate("studentNoLoanHistoryMessage")}
                </Alert>
              ) : (
                <>
                  <Toolbar>
                    <Typography variant="h5">
                      {totalLoans} {translate("loans")}
                    </Typography>
                  </Toolbar>
                  <Toolbar>
                    <LoanHistorySortControl
                      value={sort}
                      onChange={handleSortChange}
                    />
                  </Toolbar>
                  <Box>
                    {loanHistory.map((loan: HostSystemItemView) => (
                      <>
                        <Divider />
                        <LoanHistoryItem loan={loan} key={loan.id} />
                      </>
                    ))}
                  </Box>
                  {/* show pagination when requested page out of bounds */}
                  {(totalLoans > pageSize || page > pages) && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Stack spacing={2}>
                        <Pagination
                          count={pages}
                          color="primary"
                          variant="text"
                          page={page}
                          onChange={handlePageChange}
                          sx={{
                            "& .MuiPagination-ul, .MuiPagination-ul li": {
                              // overides nav ul li borders
                              border: "none",
                            },
                          }}
                        />
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
